import {
  SelectSearchOptionModel,
  SelectSearchOptionOptionalModel,
} from '@/components/form/model/SelectSearchOption.model.ts';
import { useMobileMediaQuery } from '@/utils/useMobileMediaQuery.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';

interface Props {
  customUniversity?: string;
  isInvalid: boolean;
  label: string;
  onChange: (result: {
    customUniversity?: string;
    university?: SelectSearchOptionOptionalModel;
  }) => void;
  placeholder: string;
  required?: boolean;
  selectedUniversity?: SelectSearchOptionOptionalModel;
  universities: SelectSearchOptionModel[];
}

export const SearchableUniversityField: React.FC<Props> = ({
  customUniversity,
  isInvalid,
  label,
  onChange,
  placeholder,
  required = false,
  selectedUniversity,
  universities,
}) => {
  const { t } = useTranslationWithPrefix('views.process.personalData');
  const isMobile = useMobileMediaQuery();

  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedUniversity && selectedUniversity.label) {
      setInputValue(selectedUniversity.label);
    } else if (customUniversity) {
      setInputValue(customUniversity);
    } else {
      setInputValue('');
    }
  }, [selectedUniversity, customUniversity]);

  const filteredUniversities = universities.filter(uni =>
    uni.label.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const exactMatch = universities.find(uni => uni.label.toLowerCase() === inputValue.toLowerCase());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(value.length > 0);

    if (value !== (selectedUniversity?.label || customUniversity || '')) {
      onChange({ customUniversity: undefined, university: undefined });
    }
  };

  const handleInputFocus = () => {
    if (inputValue.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleUniversitySelect = (university: SelectSearchOptionModel) => {
    setInputValue(university.label);
    setShowSuggestions(false);
    onChange({ customUniversity: undefined, university });
    inputRef.current?.blur();
  };

  const handleCustomSelect = () => {
    setShowSuggestions(false);
    onChange({ customUniversity: inputValue.trim(), university: undefined });
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setInputValue('');
    setShowSuggestions(false);
    onChange({ customUniversity: undefined, university: undefined });
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasValue = selectedUniversity || customUniversity;

  return (
    <Form.Group className="mb-3" ref={containerRef}>
      <label className="small pb-1">
        {label}
        {required ? '*' : ''}
      </label>

      <div className="position-relative">
        <div className="d-flex">
          <Form.Control
            className="flex-grow-1"
            isInvalid={isInvalid}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            ref={inputRef}
            type="text"
            value={inputValue}
          />
          {hasValue && (
            <Button className="ms-2" onClick={handleClear} size="sm" variant="outline-secondary">
              ×
            </Button>
          )}
        </div>

        {showSuggestions && (
          <div
            className="position-absolute bg-white border rounded shadow"
            style={{
              maxHeight: '200px',
              maxWidth: '400px',
              minWidth: '100%',
              overflowY: 'auto',
              top: '100%',
              width: 'max-content',
              zIndex: 1050,
            }}
          >
            {filteredUniversities.length > 0
              ? filteredUniversities.map(university => (
                  <OverlayTrigger
                    delay={{ hide: 100, show: 500 }}
                    key={university.value}
                    overlay={
                      <Tooltip id={`tooltip-${university.value}`}>{university.label}</Tooltip>
                    }
                    placement={isMobile ? 'top' : 'right'}
                  >
                    <button
                      className="dropdown-item text-start border-0 w-100 py-2 px-3"
                      onClick={() => handleUniversitySelect(university)}
                      style={{
                        cursor: 'pointer',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      type="button"
                    >
                      {university.label}
                    </button>
                  </OverlayTrigger>
                ))
              : null}

            {inputValue.trim() && !exactMatch && (
              <button
                className="dropdown-item text-start border-0 w-100 py-2 px-3 fst-italic"
                onClick={handleCustomSelect}
                style={{
                  borderTop: '1px solid #eee',
                  cursor: 'pointer',
                }}
                type="button"
              >
                {t('university.addCustom')} &#34;{inputValue.trim()}&#34;
              </button>
            )}

            {filteredUniversities.length === 0 && inputValue.trim() && exactMatch && (
              <div className="py-2 px-3 text-muted small">{t('university.selectFromList')}</div>
            )}
          </div>
        )}
      </div>
    </Form.Group>
  );
};
